import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useLocation, useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import Bottom from "@fuse/sharedComponents/bottom"
import BottomAction from "@fuse/sharedComponents/buttons/BottomAction"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import { logoText } from "@fuse/sharedComponents/legacyUI/Top/styles"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import RestrictionModal from "@fuse/sharedComponents/restrictionModal"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import {
  EEventStatus,
  EVENT_CLASS,
  EventDocData,
  EventStatusType,
} from "@letshang/constants"
import { get, isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { userState } from "@fuse/recoil/signup"

import EmailVerificationPopup from "@fuse/pages/eventPage/components/EmailVerificationPopup"
import NonTicketedEventReasonPopup from "@fuse/pages/eventPage/components/NonTicketedEventReasonPopup"
import { useEvents } from "@fuse/pages/eventPage/controller"
import EventDetails from "@fuse/pages/eventPage/EventDetails"
import FastSignUpComponent from "@fuse/pages/eventPage/FastSignUpComponent"
import EventError from "@fuse/pages/eventPage/Modal/Error"
import EventNotFound, {
  EEventModalButtons,
  EModalShowContentMap,
  EventModalContentMap,
  useShowAlertWithContentType,
} from "@fuse/pages/eventPage/Modal/EventNotFound"
import "@fuse/pages/eventPage/style.css"

import Failed from "@fuse/assets/images/signup/img-failed.svg"

const FancyLetsHang = () => {
  return (
    <FancyText className="text-[24px]" style={logoText}>
      Let's Hang!
    </FancyText>
  )
}

export const ClaimTicketLandingPage: FunctionComponent = () => {
  const eventInvitationRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const params = useParams()
  const eventId = get(params, "eventId", "")
  const phoneNumber = get(params, "phoneNumber", null)
  const [user] = useRecoilState(userState)
  const location = useLocation()
  const from = location.state?.from
  const urlParams = new URLSearchParams(window.location.search)
  const invitedId = urlParams.get("invitedId")

  useEffect(() => {
    if (from === "StripeCheckout") {
      refresh()
    }
  }, [from])

  const {
    isLoading,
    eventDetails,
    eventType,
    hasNotJoined,
    isAdmin,
    myEventActions,
    isDeclined,
    isRefunded,
    isPageLoading,
    onLeaveEvent,
    canLeaveEvent,
    onLeaveNonTicketedEvent,
    isNonTicketedReasonPopupOpen,
    onConfirmLeaveNonTicketedEvent,
    handleClosePopup,
    isUserEmailExist,
    isEmailInboxVerification,
    onVerifyEmail,
    loadingEmailVerification,
    isVerificationPopup,
    setIsVerificationPopup,
    setIsEmailInboxVerification,
    myTicketStatus,
    refundAlertVisible,
    refundReferenceNumber,
    refresh,
    isDraftEvent,
    plusOnePopupState,
    setPlusOnePopupState,
    plusOnes,
    acceptedGuestAvatars,
    onEventJoin,
    guestList,
    onClickClaimTicket,
  } = useEvents(eventId, user?.uid, invitedId)

  const eventDocData: EventDocData = get(eventDetails, "eventDocData", null)
  const flyerImage = get(eventDocData, "flyerImage", "")

  const eventStatusData = get(
    eventDocData,
    "eventStatus",
    EEventStatus.UPCOMING,
  ) as string

  const eventClass = get(
    eventDocData,
    "eventClass",
    EVENT_CLASS.NON_TICKETED_EVENT,
  ) as EVENT_CLASS
  const isTicketedEvent = eventClass === EVENT_CLASS.TICKETED_EVENT

  const [eventStatus, setEventStatus] = useState(eventType)

  const isEventExist = !isEmpty(eventDocData) || !isEmpty(eventStatus)

  const { showFastSignUpPopUp, setShowFastSignUpPopUp } = useSignUpModalState()

  const onEventDetails = () => {
    if (eventInvitationRef && eventInvitationRef.current) {
      eventInvitationRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }

  const onHomePage = () => {
    navigate(ROUTES.LANDING)
  }

  useEffect(() => {
    onEventDetails()
    if (isEventExist) {
      setEventStatus(eventType ?? EEventStatus.UPCOMING)
    } else if (eventType) {
      setEventStatus(eventType)
    }
  }, [isEventExist, eventType])

  const { showContentTypeAlert } = useShowAlertWithContentType()

  useEffect(() => {
    if (eventStatusData === EEventStatus.CLOSE && !refundAlertVisible) {
      // this is because the event is closed by host and the user or guest hadn't joined the event
      if (!hasNotJoined) {
        showContentTypeAlert({
          ...EventModalContentMap[EModalShowContentMap.CANCELLED],
          description: EventModalContentMap.cancelled.description.replace(
            "{{eventName}}",
            eventDocData?.eventName ?? "",
          ),
        })
      } else {
        showContentTypeAlert({
          ...EventModalContentMap.closed,
          buttons: [
            // EEventModalButtons.DOWNLOAD_APP,
            EEventModalButtons.GO_TO_HOMEPAGE,
          ],
        })
      }
    }

    if (refundAlertVisible) {
      showContentTypeAlert({
        ...EventModalContentMap.refundProcessed,
        description: React.cloneElement(
          EventModalContentMap.refundProcessed.description,
          {
            children: React.Children.map(
              EventModalContentMap.refundProcessed.description.props.children,
              (child) => {
                if (typeof child === "object") {
                  // Check if it's the div containing the refId
                  if (
                    child.props.className ===
                    "flex mb-4 w-[100%] justify-center items-center"
                  ) {
                    return React.cloneElement(child, {
                      children: React.Children.map(
                        child.props.children,
                        (grandChild) => {
                          if (grandChild.type === "p") {
                            return React.cloneElement(grandChild, {
                              children: refundReferenceNumber,
                            })
                          }

                          return grandChild
                        },
                      ),
                    })
                  }
                }
                return child
              },
            ),
          },
        ),
      })
    }
  }, [eventStatusData, refundAlertVisible])

  useEffect(() => {
    if (eventStatus === EEventStatus.INELIGIBLE) {
      showContentTypeAlert({
        ...EventModalContentMap[EModalShowContentMap.RESTRICTED],
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP,
          EEventModalButtons.GO_TO_HOMEPAGE,
        ],
      })
    } else if (
      eventStatus === EEventStatus.OVER ||
      eventStatus === EEventStatus.CLOSED
    ) {
      if (hasNotJoined) {
        showContentTypeAlert({
          ...EventModalContentMap[EModalShowContentMap.CLOSED],
          buttons: [
            // EEventModalButtons.DOWNLOAD_APP,
            EEventModalButtons.GO_TO_HOMEPAGE,
          ],
        })
      }
    }
  }, [eventStatus])

  useEffect(() => {
    if (myTicketStatus === EventStatusType.DECLINED) {
      // declined
      showContentTypeAlert({
        ...EventModalContentMap[EModalShowContentMap.DECLINED],
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP
        ],
      })
    }
  }, [myTicketStatus])

  useEffect(() => {
    if (isDeclined) {
      showContentTypeAlert({
        ...EventModalContentMap[EModalShowContentMap.REQ_DECLINED],
        description: EventModalContentMap[
          EModalShowContentMap.REQ_DECLINED
        ].description.replace("{{eventName}}", eventDocData?.eventName ?? ""),
        buttons: [
          // EEventModalButtons.DOWNLOAD_APP
        ],
      })
    }
  }, [isDeclined])

  const showFastSignUpPopUpCalbk = useCallback((val) => {
    setShowFastSignUpPopUp(val)
  }, [])

  const ClaimTicketAction = [
    {
      title: "Claim ticket",
      onPress: () => {
        onClickClaimTicket()
      },
    },
  ]

  const onEventAction = (index: number | undefined) => {
    if (index === undefined) return
    const action = ClaimTicketAction[index]
    if (!action) return

    if ("onPress" in action) {
      action.onPress()
    }
  }

  if (!isEventExist && !isLoading && !isPageLoading) {
    return <EventNotFound userId={user?.uid} />
  }

  if ((!eventDetails || !eventDocData) && !isLoading) {
    return <EventError />
  }
  return (
    <BackgroundWrapper bgImage={flyerImage} className=" min-h-[100vh] h-full">
      <BlurWrapper
        blurKey={blurKeys?.mainBlurView}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-[100vh] h-full flex flex-col overflow-auto pb-20 sm:pb-0"
      >
        <div id="upper-body" className=" flex-1 ">
          <div
            className={`flex flex-col w-full items-center justify-center relative z-[100] `}
          >
            <header className="flex w-full justify-between items-center self-center px-4 md:px-8 py-6 max-w-screen-xl 2xl:max-w-screen-2xl gap-7 ">
              <div>
                <FancyLetsHang />
              </div>
            </header>
          </div>

          <div id="body">
            {eventId && (isPageLoading || isLoading) && <LoadingSpinner />}

            <div className="flex-1 ">
              {isVerificationPopup && (
                <EmailVerificationPopup
                  isLoading={loadingEmailVerification}
                  isEmailInboxVerification={isEmailInboxVerification}
                  onClose={() => {
                    setIsVerificationPopup(false)
                    setIsEmailInboxVerification(false)
                  }}
                  onVerifyEmail={onVerifyEmail}
                />
              )}

              {isNonTicketedReasonPopupOpen && (
                <NonTicketedEventReasonPopup
                  isLoading={isPageLoading}
                  onClose={handleClosePopup}
                  onConfirmLeaveNonTicketedEvent={
                    onConfirmLeaveNonTicketedEvent
                  }
                />
              )}
              <div ref={eventInvitationRef}>
                {isRefunded ? (
                  <RestrictionModal
                    imageSrc={Failed}
                    title="Ticket refunded"
                    content={[
                      "Good news! Your ticket refund has been processed. ",
                      "We hope to see you at another event soon!",
                    ]}
                    buttonLabel="Go to Homepage"
                    onButtonClick={onHomePage}
                  />
                ) : null}

                <EventDetails
                  eventDetails={eventDetails}
                  eventDocData={eventDocData}
                  hasNotJoined={hasNotJoined}
                  isAdmin={isAdmin}
                  myEventActions={ClaimTicketAction}
                  onLeaveEvent={
                    isTicketedEvent ? onLeaveEvent : onLeaveNonTicketedEvent
                  }
                  myTicketStatus={myTicketStatus}
                  canLeaveEvent={canLeaveEvent}
                  isUserEmailExist={isUserEmailExist}
                  acceptedGuestAvatars={acceptedGuestAvatars as []}
                  openEmailVerification={() => setIsVerificationPopup(true)}
                  eventStatus={eventStatus}
                  isDraftEvent={isDraftEvent}
                  onEventJoin={onEventJoin}
                  guestList={guestList}
                  flyerImage={flyerImage}
                  invitedId={invitedId}
                  onEventAction={onEventAction}
                />
              </div>
            </div>
          </div>
        </div>

        {isEventExist && (
          <div id="lower-body">
            {/* <EventBanner2 eventId={eventId} flyerImage={flyerImage} /> */}
            <Bottom isEventDetailFooter={true} />
          </div>
        )}
      </BlurWrapper>

      <div className="fixed w-full bottom-0 p-4 landscape:hidden">
        {!isEmpty(myEventActions) && isEventExist && (
          <BottomAction
            bottomActionClassName={"w-full"}
            buttonWrapperClassName={"flex-1 px-4"}
            solidBtnClassName={"flex-1 !flex py-5 backdrop-blur-md rounded-lg"}
            buttonOptionList={ClaimTicketAction}
            handleScreen={onEventAction}
          />
        )}
      </div>
      <FastSignUpComponent
        isOpen={showFastSignUpPopUp}
        setOpenState={showFastSignUpPopUpCalbk}
        title={eventDocData?.eventName ?? "-"}
        phoneNumberToBePrefilled={phoneNumber}
      />
    </BackgroundWrapper>
  )
}
